import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useToast } from '@/contexts/ToastContext'
import {
  Radar,
  Play,
  Square,
  Zap,
  Sprout,
  Pause,
  RotateCcw,
  Trash2,
  Plus,
  Settings2,
  AlertTriangle,
  Loader2,
  Flame
} from 'lucide-react'
import { minerApi } from '@/services/api'
import {
  describirCapturas,
  marcaDePasada,
  pollWhileOn,
  relativeTime,
  repedirHistorial
} from '@/utils/miner'

type SeedState = 'active' | 'cooling' | 'exhausted' | 'paused'

/** Contrastes verificados sobre stone-900: todos por encima de 4.5:1. Nunca van solos. */
const SEED_STATE: Record<SeedState, { label: string; color: string; help: string }> = {
  active: { label: 'Activa', color: '#34d399', help: 'Produce capturas' },
  cooling: { label: 'Enfriándose', color: '#fbbf24', help: 'Varias pasadas sin encontrar nada' },
  exhausted: { label: 'Agotada', color: '#a8a29e', help: 'Descartada, vuelve si el mercado cambia' },
  paused: { label: 'Pausada', color: '#a8a29e', help: 'La pausaste tú o el médico, por no traer productos' }
}

const SOURCE_LABEL: Record<string, string> = {
  bootstrap: 'arranque',
  manual: 'tuya',
  harvested: 'aprendida',
  winner: 'ganador fichado',
  explored: 'explorada',
  inspired: 'inspirada en ganador',
  pattern: 'por patrón',
  vein: 'de veta'
}

/**
 * Traduce el tope de créditos a lenguaje llano.
 *
 * Se duplica a propósito: así el usuario ve el efecto de lo que escribe al instante, sin
 * una ida y vuelta al servidor por cada tecla.
 */
function pacingText(budget: number): string {
  if (!budget || budget < 1) return 'Sin presupuesto no buscará nada.'

  return `Irá a toda velocidad hasta gastar ${budget} créditos o quedarse sin términos nuevos que probar. Lo que ocurra antes.`
}

function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded-xl border border-stone-700 bg-stone-900 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-stone-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-stone-100">{value}</div>
      {hint && <div className="text-xs text-stone-500">{hint}</div>}
    </div>
  )
}

/**
 * Medidor del presupuesto del día.
 *
 * Es lo primero que se mira en esta pantalla, así que va arriba y grande: un minero que
 * gasta sin que lo veas es exactamente lo que no queremos.
 */
function BudgetMeter({ spent, daily }: { spent: number; daily: number }) {
  const pct = daily > 0 ? Math.min(100, (spent / daily) * 100) : 0
  const exhausted = daily > 0 && spent >= daily
  const nearLimit = pct >= 80 && !exhausted

  const color = exhausted ? '#fb7185' : nearLimit ? '#fbbf24' : '#e879f9'

  return (
    <div className="rounded-xl border border-stone-700 bg-stone-900 p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wide text-stone-500">Presupuesto de hoy</span>
        <span className="text-sm text-stone-300">
          <span className="font-semibold text-stone-100">{spent}</span> / {daily} créditos
        </span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-stone-800">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>

      {exhausted && (
        <p className="mt-2 flex items-center gap-1.5 text-xs" style={{ color }}>
          <AlertTriangle className="h-3.5 w-3.5" />
          Tope alcanzado. El minero está parado hasta mañana.
        </p>
      )}
      {nearLimit && (
        <p className="mt-2 text-xs" style={{ color }}>
          Queda poco margen para hoy.
        </p>
      )}
    </div>
  )
}

export default function MinerPage() {
  const { showSuccess, showError, showInfo } = useToast()
  const queryClient = useQueryClient()
  // "vivas" por defecto: lo que busca ahora, lo más nuevo primero. "Todas" es el historial.
  const [seedFilter, setSeedFilter] = useState('vivas')
  const [showSettings, setShowSettings] = useState(false)
  const [newTerms, setNewTerms] = useState('')
  /** Tope tecleado, para previsualizar el ritmo sin guardar todavía. */
  const [previewBudget, setPreviewBudget] = useState<number | null>(null)

  // Una sola consulta se refresca sola, y solo con el minero encendido. Las otras dos se
  // piden al entrar y cuando algo cambia: tres sondeos en paralelo cada minuto agotaban la
  // cuota de peticiones de la API y acababan devolviendo 429 a toda la aplicación.
  /**
   * El global de la app da los datos por frescos 5 minutos, así que al entrar a esta
   * pestaña no se pedía nada: veías el estado de hace un rato y las pasadas del minero
   * ocurrían sin que se notara. Aquí no vale, porque esto cambia solo.
   *
   * `refetchOnMount: 'always'` + `staleTime: 0` revalidan al entrar, y como React Query
   * sigue mostrando lo cacheado mientras pide, no hay parpadeo. El sondeo se pausa solo
   * cuando la pestaña no está a la vista.
   */
  const revalidate = {
    staleTime: 0,
    refetchOnMount: 'always' as const,
    refetchInterval: pollWhileOn,
    refetchIntervalInBackground: false
  }

  // El tipo explícito evita que `revalidate` —compartido por las tres— fuerce a TypeScript
  // a deducir un mismo tipo de datos para todas. Las respuestas de la API no están tipadas.
  const { data: status, isLoading, isFetching } = useQuery<any>('miner-status', minerApi.status, revalidate)
  const { data: seedsData } = useQuery<any>(
    ['miner-seeds', seedFilter],
    () => minerApi.seeds(seedFilter),
    revalidate
  )
  const { data: runsData } = useQuery<any>('miner-runs', () => minerApi.runs(10), revalidate)
  const { data: settingsData } = useQuery('miner-settings', minerApi.getSettings, {
    enabled: showSettings
  })

  const refresh = () => {
    queryClient.invalidateQueries('miner-status')
    queryClient.invalidateQueries('miner-seeds')
    queryClient.invalidateQueries('miner-runs')
  }

  /**
   * El historial y las semillas se enteran de una pasada nueva sin sondear.
   *
   * Solo el estado repregunta cada 15 s, y trae la última pasada: hace de aviso. Cuando su
   * marca cambia —al arrancar la pasada y otra vez al terminarla— se piden esas dos una
   * vez. Mientras no pase nada, cero peticiones. La decisión vive en `utils/miner` para
   * poder comprobarla sin montar React.
   */
  const marcaUltimaPasada = marcaDePasada(status?.lastRun)
  /** La última marca ya pedida. Un ref y no un estado: cambiarla no debe repintar nada. */
  const marcaPedida = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!marcaUltimaPasada) return
    const repedir = repedirHistorial(marcaUltimaPasada, marcaPedida.current)
    marcaPedida.current = marcaUltimaPasada
    if (!repedir) return
    queryClient.invalidateQueries('miner-runs')
    queryClient.invalidateQueries('miner-seeds')
  }, [marcaUltimaPasada, queryClient])

  const toggleMutation = useMutation((on: boolean) => (on ? minerApi.start() : minerApi.stop()), {
    onSuccess: (data: any) => {
      showSuccess(data.message)
      refresh()
    },
    onError: () => {
      showError('No se pudo cambiar el estado del minero')
    }
  })

  const sweepMutation = useMutation(minerApi.sweep, {
    onSuccess: (r: any) => {
      if (!r.ran) showInfo(r.reason || 'No se ejecutó')
      else
        showSuccess(
          `${r.seedsRun} semillas · ${describirCapturas(r.captures, r.kept)} · ` +
            `${r.creditsSpent} créditos`
        )
      refresh()
    },
    onError: () => {
      showError('La pasada falló')
    }
  })

  const addSeedsMutation = useMutation(
    (terms: string[]) => minerApi.addSeeds({ terms }),
    {
      onSuccess: (r: any) => {
        showSuccess(r.message)
        setNewTerms('')
        refresh()
      },
      onError: () => {
        showError('No se pudieron añadir las semillas')
      }
    }
  )

  const seedStateMutation = useMutation(
    ({ id, state }: { id: string; state: 'active' | 'paused' }) => minerApi.setSeedState(id, state),
    {
      onSuccess: () => refresh(),
      onError: () => {
        showError('No se pudo cambiar la semilla')
      }
    }
  )

  const deleteSeedMutation = useMutation((id: string) => minerApi.deleteSeed(id), {
    onSuccess: () => refresh(),
    onError: () => {
      showError('No se pudo eliminar')
    }
  })

  const settingsMutation = useMutation(
    (s: Record<string, unknown>) => minerApi.updateSettings(s),
    {
      onSuccess: () => {
        showSuccess('Ajustes guardados')
        queryClient.invalidateQueries('miner-settings')
        queryClient.invalidateQueries('miner-status')
      },
      onError: () => {
        showError('No se pudieron guardar los ajustes')
      }
    }
  )

  const on = !!status?.enabled
  // Barrer a mano y barrer por el cron se ven igual desde la pantalla: en ambos casos hay
  // una pasada en curso. `scheduler.running` es del proceso entero, no de este usuario,
  // así que solo se muestra si el minero de este usuario está encendido.
  const sweeping = sweepMutation.isLoading || (on && !!status?.scheduler?.running)

  // El médico del servidor revisa cada 5 min y arregla solo lo que puede. "Presupuesto
  // cumplido" no es un problema: es el tope que pusiste haciendo su trabajo.
  const medico = status?.medico
  const diagnostico: string = medico?.ultimo?.estado ?? ''
  const enProblemas =
    !!diagnostico && diagnostico !== 'sano' && !diagnostico.startsWith('presupuesto')
  const seeds = seedsData?.data || []
  const settings = settingsData?.settings
  const lifetime = status?.lifetime

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-stone-100">
            <Radar className="h-6 w-6" style={{ color: on ? '#34d399' : '#a8a29e' }} />
            Minero autónomo
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-stone-400">
            Busca solo, sin parar, y aprende de lo que encuentra: las consultas que dan
            ganadores suben, las que no dan nada se enfrían. Nunca pasa del tope de créditos
            que le pongas.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => sweepMutation.mutate()}
            disabled={sweepMutation.isLoading}
            className="flex items-center gap-2 rounded-xl border border-stone-600 bg-stone-900 px-3 py-2 text-sm text-stone-200 hover:border-stone-500 disabled:opacity-50"
          >
            <Zap className="h-4 w-4" />
            {sweepMutation.isLoading ? 'Barriendo…' : 'Barrer ahora'}
          </button>

          <button
            onClick={() => setShowSettings(v => !v)}
            className="flex items-center gap-2 rounded-xl border border-stone-600 bg-stone-900 px-3 py-2 text-sm text-stone-200 hover:border-stone-500"
          >
            <Settings2 className="h-4 w-4" />
            Ajustes
          </button>

          <button
            onClick={() => toggleMutation.mutate(!on)}
            disabled={toggleMutation.isLoading}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
              on ? 'bg-rose-700 hover:bg-rose-600' : 'bg-emerald-700 hover:bg-emerald-600'
            }`}
          >
            {on ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {on ? 'Apagar' : 'Encender'}
          </button>
        </div>
      </div>

      {isLoading && <div className="py-12 text-center text-stone-400">Cargando…</div>}

      {status && (
        <>
          <div className="grid gap-3 lg:grid-cols-[2fr_3fr]">
            <BudgetMeter spent={status.budget.spentToday} daily={status.budget.daily} />

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {/* El número grande es lo que SIGUE EN PIE, no lo que se llegó a insertar: el
                  clasificador descarta después de capturar, y contar lo insertado presumía
                  de un botín que la pestaña de capturas ya no tenía. */}
              <Stat
                label="Capturas vivas"
                value={lifetime?.kept ?? lifetime?.captures ?? 0}
                hint={
                  lifetime?.captures
                    ? `de ${lifetime.captures} capturadas`
                    : 'en total'
                }
              />
              <Stat
                label="Coste"
                value={lifetime?.creditsPerCapture ?? '—'}
                hint="créditos por captura viva"
              />
              <Stat label="Anuncios vistos" value={lifetime?.adsSeen ?? 0} />
              <Stat label="Pasadas" value={lifetime?.runs ?? 0} />
            </div>
          </div>

          {!on ? (
            <div className="rounded-xl border border-stone-700 bg-stone-900 px-4 py-3 text-sm text-stone-400">
              El minero está apagado: no gasta ni un crédito. Al encenderlo hace una primera
              pasada al momento, y si no tienes semillas se crean unas de arranque.
            </div>
          ) : (
            <div className="rounded-xl border border-stone-700 bg-stone-900 px-4 py-3 text-sm text-stone-400">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {sweeping ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: '#34d399' }} />
                    <span className="text-stone-100">Barriendo ahora…</span>
                    <span>Consultando la Ad Library. Los números de arriba se mueven solos.</span>
                  </>
                ) : (
                  <>
                    {/* El punto verde decía "todo bien" también las 17 horas en pausa y las
                        3,5 atascado. Ahora lo pinta el diagnóstico del médico del servidor. */}
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ background: enProblemas ? '#f59e0b' : '#34d399' }}
                    />
                    <span className="text-stone-200">Encendido.</span>
                    {enProblemas ? (
                      <span style={{ color: '#fbbf24' }}>{diagnostico}</span>
                    ) : status.outOfBudget ? (
                      <span>Presupuesto de hoy agotado. Sube el tope o espera a mañana.</span>
                    ) : status.hasSeeds ? (
                      <span className="text-stone-300">Buscando. Le quedan términos por revisar.</span>
                    ) : status.seedsLive > 0 ? (
                      <span>
                        Al día: ya repasó tus {status.seedsLive} términos. Volverá a mirarlos
                        cuando haya anuncios nuevos que ver. Para encontrar más, añade términos.
                      </span>
                    ) : (
                      <span>Sin términos que probar. Añade alguno abajo.</span>
                    )}
                  </>
                )}
              </div>

              {/* Qué trajo la última pasada. Sin esto la pantalla parecía congelada: la hora
                  suelta no decía si fue hace un minuto o hace tres horas, ni si sirvió. */}
              {status.lastRun && (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-stone-500">
                  <span>Última pasada {relativeTime(status.lastRun.startedAt)}:</span>
                  <span>{status.lastRun.totals?.adsSeen ?? 0} anuncios vistos</span>
                  <span>·</span>
                  <span
                    className={status.lastRun.totals?.captures ? 'font-medium' : undefined}
                    style={status.lastRun.totals?.captures ? { color: '#fbbf24' } : undefined}
                  >
                    {status.lastRun.totals?.captures ? (
                      <Flame className="mr-1 inline h-3 w-3" />
                    ) : null}
                    {describirCapturas(
                      status.lastRun.totals?.captures,
                      status.lastRun.totals?.kept
                    )}
                  </span>
                  <span>·</span>
                  <span>{status.lastRun.totals?.creditsSpent ?? 0} créditos</span>
                  {isFetching && !sweeping && (
                    <span className="ml-auto flex items-center gap-1 text-stone-600">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      actualizando
                    </span>
                  )}
                </div>
              )}

              {/* Lo que el minero se hizo a sí mismo. No es un aviso para que actúes: es el
                  registro de lo que ya arregló, y el coste por producto que persigue. */}
              {medico?.ultimo && (
                <div className="mt-1.5 text-xs text-stone-500">
                  <span>
                    Últimas 3 h: {medico.ultimo.caras3h} productos nuevos
                    {medico.ultimo.crPorProducto != null && (
                      <> · {medico.ultimo.crPorProducto} cr por producto</>
                    )}
                  </span>
                  {medico.historial?.slice(0, 3).map((h: { at: string; texto: string }) => (
                    <div key={h.at + h.texto} className="mt-0.5 text-stone-400">
                      🩺 {relativeTime(h.at)}: {h.texto}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showSettings && settings && (
        <div className="rounded-xl border border-stone-700 bg-stone-900 p-4">
          <h2 className="mb-1 text-sm font-semibold text-stone-100">Cómo trabaja</h2>
          <p className="mb-4 text-xs text-stone-400">
            Solo decides cuánto estás dispuesto a gastar. No va despacio a propósito: los
            límites de ritmo los gestiona el proveedor de la API, que es lo que pagas con
            los créditos.
          </p>

          <form
            onSubmit={e => {
              e.preventDefault()
              const f = new FormData(e.currentTarget)

              settingsMutation.mutate({
                dailyCreditBudget: Number(f.get('dailyCreditBudget')),
                harvestSeeds: f.get('harvestSeeds') === 'on',
                language: String(f.get('language') || '').trim().toUpperCase(),
                mineWinnerAdvertisers: f.get('mineWinnerAdvertisers') === 'on',
                verifyStale: f.get('verifyStale') === 'on',
                verifyStaleDays: Number(f.get('verifyStaleDays')),
                verifyPerSweep: Number(f.get('verifyPerSweep')),
                winnerPeakForAdvertiser: Number(f.get('winnerPeakForAdvertiser')),
                winnerActiveAdsForAdvertiser: Number(f.get('winnerActiveAdsForAdvertiser')),
                autoPace: false, // los valores del avanzado se respetan tal cual
                concurrency: Number(f.get('concurrency')),
                seedsPerSweep: Number(f.get('seedsPerSweep')),
                seedCooldownMinutes: Number(f.get('seedCooldownMinutes')),
                maxConsecutiveEmpty: Number(f.get('maxConsecutiveEmpty')),
                maxSeeds: Number(f.get('maxSeeds')),
                countries: String(f.get('countries') || '')
                  .split(',')
                  .map(c => c.trim().toUpperCase())
                  .filter(Boolean)
              })
            }}
          >
            {/* step="1" en todos los numéricos: con un step mayor y min="1", el navegador
                solo admite 1, 11, 21… y rechaza 200 o 300. Peor aún, un campo inválido
                dentro del <details> plegado bloquea el envío sin poder mostrar el aviso. */}
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-sm text-stone-300">Créditos al día como máximo</span>
                <input
                  name="dailyCreditBudget"
                  type="number"
                  min="1"
                  step="1"
                  defaultValue={settings.dailyCreditBudget}
                  onChange={e => setPreviewBudget(Number(e.target.value))}
                  className="w-full rounded-xl border border-stone-600 bg-stone-950 px-3 py-2 text-stone-100 focus:border-secondary-400 focus:ring-2 focus:ring-secondary-500/30"
                />
                <span className="mt-1 block text-xs text-stone-500">
                  {pacingText(previewBudget ?? settings.dailyCreditBudget)}
                </span>
              </label>

              <label className="block">
                <span className="mb-1 block text-sm text-stone-300">Idioma de los anuncios</span>
                <input
                  name="language"
                  defaultValue={settings.language || 'ES'}
                  maxLength={2}
                  className="w-full rounded-xl border border-stone-600 bg-stone-950 px-3 py-2 uppercase text-stone-100 focus:border-secondary-400 focus:ring-2 focus:ring-secondary-500/30"
                />
                <span className="mt-1 block text-xs text-stone-500">
                  Con el idioma puesto, el minero busca en TODOS los países a la vez sin
                  llenarse de inglés. Medido sobre un término real: 4.474 anuncios así,
                  frente a 1.025 buscando solo en Colombia, y solo 6 de 29 se repetían.
                </span>
              </label>
            </div>

            <label className="mt-4 flex items-start gap-2 text-sm text-stone-300">
              <input
                name="harvestSeeds"
                type="checkbox"
                defaultChecked={settings.harvestSeeds}
                className="mt-0.5 h-4 w-4 rounded border-stone-600 bg-stone-950"
              />
              <span>
                Aprender solo
                <span className="block text-xs text-stone-500">
                  Saca términos nuevos del vocabulario de los ganadores que va capturando.
                </span>
              </span>
            </label>

            <label className="mt-3 flex items-start gap-2 text-sm text-stone-300">
              <input
                name="mineWinnerAdvertisers"
                type="checkbox"
                defaultChecked={settings.mineWinnerAdvertisers}
                className="mt-0.5 h-4 w-4 rounded border-stone-600 bg-stone-950"
              />
              <span>
                Perseguir a los anunciantes ganadores
                <span className="block text-xs text-stone-500">
                  Cuando alguien demuestra que escala, le pide el catálogo entero. Un término
                  devuelve 30 anuncios de desconocidos; un anunciante probado devuelve todo
                  lo suyo por el mismo crédito.
                </span>
              </span>
            </label>

            <label className="mt-3 flex items-start gap-2 text-sm text-stone-300">
              <input
                name="verifyStale"
                type="checkbox"
                defaultChecked={settings.verifyStale}
                className="mt-0.5 h-4 w-4 rounded border-stone-600 bg-stone-950"
              />
              <span>
                Confirmar que lo capturado sigue vivo
                <span className="block text-xs text-stone-500">
                  Las búsquedas solo devuelven anuncios activos, así que uno apagado
                  desaparece sin avisar y la ficha se quedaría diciendo "Subiendo" para
                  siempre. Esto lo pregunta directamente cada pocos días.
                </span>
              </span>
            </label>

            <details className="mt-4 rounded-xl border border-stone-800 bg-stone-950/40">
              <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-wide text-stone-500 hover:text-stone-300">
                Avanzado
              </summary>

              <div className="border-t border-stone-800 p-3">
                <p className="mb-3 text-xs text-stone-500">
                  No hay temporizadores ni enfriamientos que bloqueen: el minero busca sin
                  parar hasta gastar el presupuesto. Un término recién buscado puntúa bajo
                  para que rote hacia lo inexplorado, pero nunca queda vetado por tiempo.
                </p>

                <label className="mb-3 block">
                  <span className="mb-1 block text-xs text-stone-400">
                    Países para los términos que añadas a mano (separados por coma)
                  </span>
                  <input
                    name="countries"
                    defaultValue={(settings.countries || []).join(', ')}
                    className="w-full rounded-lg border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100"
                  />
                </label>

                <div className="grid gap-3 md:grid-cols-3">
                  {[
                    { key: 'seedCooldownMinutes', label: 'Horizonte de frescura (min)', step: '1' },
                    { key: 'concurrency', label: 'Búsquedas simultáneas', step: '1' },
                    { key: 'seedsPerSweep', label: 'Máximo por pasada', step: '1' },
                    { key: 'maxConsecutiveEmpty', label: 'Pasadas vacías antes de enfriar', step: '1' },
                    { key: 'maxSeeds', label: 'Máximo de términos', step: '1' },
                    { key: 'winnerPeakForAdvertiser', label: 'Copias para perseguir a un anunciante', step: '1' },
                    {
                      key: 'winnerActiveAdsForAdvertiser',
                      label: 'Anuncios activos para perseguirlo igualmente (0 = apagado)',
                      step: '1'
                    },
                    { key: 'verifyStaleDays', label: 'Días sin verse para volver a preguntar', step: '1' },
                    { key: 'verifyPerSweep', label: 'Capturas a confirmar por pasada', step: '1' }
                  ].map(f => (
                    <label key={f.key} className="block">
                      <span className="mb-1 block text-xs text-stone-400">{f.label}</span>
                      <input
                        name={f.key}
                        type="number"
                        step={f.step}
                        min="1"
                        defaultValue={settings[f.key]}
                        className="w-full rounded-lg border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </details>

            <button
              type="submit"
              disabled={settingsMutation.isLoading}
              className="mt-4 rounded-xl bg-secondary-600 px-4 py-2 text-sm font-medium text-white hover:bg-secondary-500 disabled:opacity-50"
            >
              {settingsMutation.isLoading ? 'Guardando…' : 'Guardar ajustes'}
            </button>
          </form>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <div className="rounded-xl border border-stone-700 bg-stone-900">
          <div className="flex flex-wrap items-center gap-2 border-b border-stone-800 p-4">
            <h2 className="flex items-center gap-2 font-semibold text-stone-100">
              <Sprout className="h-4 w-4" />
              Qué está buscando
            </h2>
            <select
              value={seedFilter}
              onChange={e => setSeedFilter(e.target.value)}
              className="ml-auto rounded-lg border border-stone-600 bg-stone-950 px-2 py-1 text-sm text-stone-100"
            >
              <option value="vivas">Vivas · las más nuevas primero</option>
              <option value="all">Todas · por capturas</option>
              {(Object.keys(SEED_STATE) as SeedState[]).map(s => (
                <option key={s} value={s}>
                  {SEED_STATE[s].label}
                </option>
              ))}
            </select>
          </div>

          <form
            className="flex gap-2 border-b border-stone-800 p-4"
            onSubmit={e => {
              e.preventDefault()
              const terms = newTerms
                .split(/[\n,]/)
                .map(t => t.trim())
                .filter(t => t.length >= 3)
              if (terms.length === 0) return showError('Escribe al menos un término')
              addSeedsMutation.mutate(terms)
            }}
          >
            <input
              value={newTerms}
              onChange={e => setNewTerms(e.target.value)}
              placeholder="Añadir términos, separados por coma"
              className="flex-1 rounded-xl border border-stone-600 bg-stone-950 px-3 py-2 text-sm text-stone-100 placeholder-stone-500 focus:border-secondary-400"
            />
            <button
              type="submit"
              disabled={addSeedsMutation.isLoading}
              className="flex items-center gap-1.5 rounded-xl bg-stone-800 px-3 py-2 text-sm text-stone-100 hover:bg-stone-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Añadir
            </button>
          </form>

          {seeds.length === 0 ? (
            <p className="p-8 text-center text-sm text-stone-500">
              Sin semillas todavía. Enciende el minero y se crean solas.
            </p>
          ) : (
            <div className="max-h-[32rem] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-stone-900 text-xs uppercase tracking-wide text-stone-500">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Término</th>
                    <th className="px-2 py-2 text-right font-medium">Pasadas</th>
                    <th className="px-2 py-2 text-right font-medium">Vivas</th>
                    <th className="px-2 py-2 text-right font-medium">Rinde</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {seeds.map((seed: any) => {
                    const st = SEED_STATE[seed.state as SeedState] || SEED_STATE.active
                    return (
                      <tr key={seed._id} className="border-t border-stone-800">
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1.5">
                            {/* Una semilla de anunciante guarda el pageId en `term`, que
                                como etiqueta es un número sin significado. */}
                            {seed.kind === 'advertiser' && (
                              <span
                                className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                                style={{ background: '#fbbf241a', color: '#fbbf24' }}
                                title="Anunciante ganador: se le pide el catálogo entero"
                              >
                                anunciante
                              </span>
                            )}
                            <span className="truncate text-stone-100">
                              {seed.kind === 'advertiser' ? seed.pageName || seed.term : seed.term}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-stone-500">
                            <span
                              className="inline-block h-1.5 w-1.5 rounded-full"
                              style={{ background: st.color }}
                            />
                            <span style={{ color: st.color }}>{st.label}</span>
                            <span>
                              · {seed.country}
                              {seed.language ? `/${seed.language}` : ''}
                            </span>
                            <span>· {SOURCE_LABEL[seed.source] || seed.source}</span>
                            {/* Por dónde va recorriendo su territorio: sin esto no hay forma
                                de ver si avanza o si se ha quedado releyendo la página 1. */}
                            {seed.kind !== 'advertiser' && (
                              <span
                                title={
                                  seed.sortBy === 'recent'
                                    ? 'Recorriendo los lanzamientos recientes'
                                    : 'Recorriendo los que más impresiones acumulan'
                                }
                              >
                                · {seed.sortBy === 'recent' ? 'recientes' : 'impresiones'}
                              </span>
                            )}
                            {seed.stats?.pageDepth > 0 && (
                              <span title="Páginas que lleva avanzadas sin repetir anuncios">
                                · pág. {seed.stats.pageDepth + 1}
                              </span>
                            )}
                            {seed.axisWindow > 0 && (
                              <span
                                style={{ color: '#a78bfa' }}
                                title="Buscando en un mes anterior, para alcanzar los anuncios antiguos que siguen activos y la búsqueda normal entierra"
                              >
                                · hace {seed.axisWindow} mes{seed.axisWindow > 1 ? 'es' : ''}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right text-stone-400">{seed.stats?.sweeps ?? 0}</td>
                        {/* Vivas y, al lado, lo que se descartó de esta semilla. Con el bruto
                            solo, un término que trae basura parecía el mejor del vivero. */}
                        <td className="px-2 py-2 text-right font-semibold text-stone-100">
                          {seed.kept ?? seed.stats?.captures ?? 0}
                          {seed.stats?.dismissed > 0 && (
                            <span
                              className="ml-1 text-xs font-normal text-stone-600"
                              title={`${seed.stats.dismissed} descartadas de ${seed.stats.captures ?? 0} capturadas`}
                            >
                              −{seed.stats.dismissed}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right text-stone-400">
                          {seed.captureRate ?? '—'}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() =>
                                seedStateMutation.mutate({
                                  id: seed._id,
                                  state: seed.state === 'paused' ? 'active' : 'paused'
                                })
                              }
                              className="rounded p-1 text-stone-500 hover:bg-stone-800 hover:text-stone-200"
                              aria-label={seed.state === 'paused' ? 'Reactivar' : 'Pausar'}
                            >
                              {seed.state === 'paused' ? (
                                <RotateCcw className="h-3.5 w-3.5" />
                              ) : (
                                <Pause className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <button
                              onClick={() => deleteSeedMutation.mutate(seed._id)}
                              className="rounded p-1 text-stone-500 hover:bg-stone-800 hover:text-stone-200"
                              aria-label="Eliminar semilla"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-stone-700 bg-stone-900">
          <h2 className="border-b border-stone-800 p-4 font-semibold text-stone-100">
            Últimas pasadas
          </h2>

          {(runsData?.data || []).length === 0 ? (
            <p className="p-8 text-center text-sm text-stone-500">Todavía no ha barrido nada.</p>
          ) : (
            <ul className="max-h-[32rem] divide-y divide-stone-800 overflow-y-auto">
              {(runsData?.data || []).map((run: any) => (
                <li key={run._id} className="px-4 py-3">
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-stone-300">
                      {new Date(run.startedAt).toLocaleString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className="text-xs text-stone-500">
                      {run.trigger === 'manual' ? 'manual' : 'automática'}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-stone-400">
                    <span className="font-semibold text-stone-200">
                      {describirCapturas(run.totals?.captures, run.totals?.kept)}
                    </span>{' '}
                    · {run.totals?.adsSeen ?? 0} anuncios ·{' '}
                    {run.totals?.creditsSpent ?? 0} créditos
                    {run.totals?.seedsHarvested > 0 && (
                      <span style={{ color: '#34d399' }}> · +{run.totals.seedsHarvested} aprendidas</span>
                    )}
                  </div>
                  {run.seedsRun?.length > 0 && (
                    <div className="mt-1 truncate text-xs text-stone-600" title={run.seedsRun.map((s: any) => s.term).join(', ')}>
                      {run.seedsRun.map((s: any) => s.term).join(' · ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
